<template>
  <div class="startup-overview">
    <div class="row">
      <div class="col-md-12">
        <div class="dashboard-box">
          <h2>Investment focus:</h2>
          <div class="row">
            <div class="col-lg-12">
              <div class="row">
                <div class="col-md-3 col-lg-3">
                  <p class="dashboard-label">Investment</p>
                </div>
                <div class="col-md-9 col-lg-9">
                  <p class="dashboard-value">
                    <template v-if="editable">
                      <input
                        v-money="{ prefix: 'Min: ' }"
                        class="form-control overview-input"
                        placeholder="Minimum investment"
                        :value="investor.investment_min"
                        @blur="onInvestmentMinChange($event.target.value)"
                      />
                      <input
                        v-money="{ prefix: 'Max: ' }"
                        class="form-control overview-input"
                        placeholder="Maximum investment"
                        :value="investor.investment_max"
                        @blur="onInvestmentMaxChange($event.target.value)"
                      />
                      <br />
                    </template>
                    <InlineRange
                      :min="investor.investment_min"
                      :max="investor.investment_max"
                    />
                  </p>
                </div>
              </div>
              <div v-if="editable || investor.stages.length > 0" class="row">
                <div class="col-md-3 col-lg-3">
                  <p class="dashboard-label">Company stages</p>
                </div>
                <div class="col-md-9 col-lg-9 values">
                  <SimpleItemList
                    v-if="!editable"
                    tag="span"
                    class="dashboard-value"
                    :items="investorCompanyStages"
                  />
                  <StageDropdown
                    v-if="editable"
                    class="seamless overview"
                    :value="investor.stages"
                    @change="onCompanyStagesChange"
                  />
                </div>
              </div>
              <div
                v-if="editable || investor.round_stages.length > 0"
                class="row"
              >
                <div class="col-md-3 col-lg-3">
                  <p class="dashboard-label">Round stages</p>
                </div>
                <div class="col-md-9 col-lg-9 values">
                  <SimpleItemList
                    v-if="!editable"
                    tag="span"
                    class="dashboard-value"
                    :items="investorRoundStages"
                  />
                  <RoundStageDropdown
                    v-if="editable"
                    class="seamless overview"
                    :single-choice="false"
                    :value="investor.round_stages"
                    @change="onRoundStagesChange"
                  />
                </div>
              </div>
              <div
                v-if="editable || investor.business_types.length"
                class="row"
              >
                <div class="col-md-3 col-lg-3">
                  <p class="dashboard-label">Bussiness Type</p>
                </div>
                <div class="col-md-9 col-lg-9 values">
                  <template v-if="editable">
                    <BusinessTypeDropdown
                      class="seamless overview"
                      :value="investor.business_types"
                      @change="onBusinessTypeChange"
                    />
                  </template>
                  <template v-if="!editable">
                    <SimpleItemList
                      tag="span"
                      class="dashboard-value"
                      :items="investor.business_types"
                    />
                  </template>
                </div>
              </div>
              <div v-if="editable || investor.product_type" class="row">
                <div class="col-md-3 col-lg-3">
                  <p class="dashboard-label">Product Type</p>
                </div>
                <div class="col-md-9 col-lg-9 values">
                  <ProductTypeDropdown
                    class="seamless overview"
                    :value="investor.product_types"
                    :single-choice="false"
                    @change="onProductTypesChange"
                  />
                  <SimpleItemList
                    v-if="!editable"
                    tag="span"
                    class="dashboard-value"
                    :items="investor.product_types"
                  />
                </div>
              </div>
              <div v-if="editable || investor.regions.length > 0" class="row">
                <div class="col-md-3 col-lg-3">
                  <p class="dashboard-label">Regions</p>
                </div>
                <div class="col-md-9 col-lg-9 values">
                  <SimpleItemList
                    v-if="!editable"
                    tag="span"
                    class="dashboard-value"
                    :items="investorRegions"
                  />
                  <RegionDropdown
                    v-if="editable"
                    :single-choice="false"
                    class="seamless overview"
                    :value="investor.regions"
                    @change="
                      updateField(
                        'regions',
                        $event.map((c) => c.value),
                      )
                    "
                  />
                </div>
              </div>

              <div v-if="editable || investor.sectors.length > 0" class="row">
                <div class="col-md-3 col-lg-3">
                  <p class="dashboard-label">Sectors</p>
                </div>
                <div class="col-md-9 col-lg-9 values">
                  <SectorDropdown
                    v-if="editable"
                    class="seamless overview"
                    :value="investor.sectors"
                    @change="
                      updateField(
                        'sectors',
                        $event.map((c) => c.value),
                      )
                    "
                  />
                  <SimpleItemList
                    v-if="!editable"
                    tag="span"
                    class="dashboard-value"
                    :items="investorSectors"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { sectorLabel } from '@/services/sectors'
import { stageLabel } from '@/services/stages'
import { roundTypeLabel } from '@/services/investment-rounds'
import { countryName } from '@/services/countries'
import { roundStageLabel } from '@/services/round-stages'
import { regionName } from 'services/regions'
import InlineRange from '@/components/InlineRange'
import SectorDropdown from '@/components/SectorDropdown'
import BusinessTypeDropdown from '@/components/BusinessTypeDropdown'
import ProductTypeDropdown from '@/components/ProductTypeDropdown'
import StageDropdown from '@/components/StageDropdown'
import RoundStageDropdown from '@/components/RoundStageDropdown'
import RegionDropdown from 'components/RegionDropdown.vue'
import * as updater from './updater'
import { moneyToNumber } from '@/filters'
import { mapState } from 'vuex'

export default {
  filters: { stageLabel, roundTypeLabel, countryName },
  components: {
    BusinessTypeDropdown,
    InlineRange,
    ProductTypeDropdown,
    StageDropdown,
    RegionDropdown,
    RoundStageDropdown,
    SectorDropdown,
  },
  props: {
    editable: { type: Boolean, default: false },
  },
  computed: {
    investorSectors() {
      return this.investor.sectors.map((code) => sectorLabel(code))
    },
    investorCompanyStages() {
      return this.investor.stages.map((code) => stageLabel(code))
    },
    investorRoundStages() {
      return this.investor.round_stages.map((code) => roundStageLabel(code))
    },
    investorRegions() {
      return this.investor.regions.map((code) => regionName(code))
    },
    ...mapState({
      investor: (state) => state.investorProfile.investor,
    }),
  },
  methods: {
    updateField(field, value) {
      return updater.updateField(field, value)
    },
    onBusinessTypeChange(choices) {
      const businessTypes = choices.map((c) => c.value)
      updater.updateField('business_types', businessTypes)
    },
    onCompanyStagesChange(choices) {
      const stages = choices.map((c) => c.value)
      updater.updateField('stages', stages)
    },
    onInvestmentMinChange(value) {
      value = moneyToNumber(value)
      if (value === this.investor.investment_min) return
      updater.updateFields({
        investment_min: value,
        investment_max: this.investor.investment_max,
      })
    },
    onInvestmentMaxChange(value) {
      value = moneyToNumber(value)
      if (value === this.investor.investment_max) return
      updater.updateFields({
        investment_min: this.investor.investment_min,
        investment_max: value,
      })
    },
    onProductTypesChange(choices) {
      const productTypes = choices.map((c) => c.value)
      updater.updateField('product_types', productTypes)
    },
    onRoundStagesChange(choices) {
      const stages = choices.map((c) => c.value)
      updater.updateField('round_stages', stages)
    },
  },
}
</script>

<style scoped lang="scss">
@import '~assets/css/vars.scss';
@import '~assets/css/colors.scss';

.values {
  margin: 4px 0 20px 0;
}
</style>
